import React from 'react';

/**
 * App-level React error boundary. Catches render-time exceptions in
 * children, prevents the whole tree from blanking, and logs to a
 * server endpoint when available.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Best-effort report — never throw from the handler itself.
    try {
      fetch('/api/errors/client', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: String(error?.message || error),
          stack: String(error?.stack || ''),
          component_stack: info?.componentStack || '',
          url: typeof window !== 'undefined' ? window.location.href : '',
        }),
      }).catch(() => {});
    } catch (_) {}
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught:', error, info);
  }

  reset = () => this.setState({ error: null });

  render() {
    if (this.state.error) {
      return (
        <div role="alert" style={{
          maxWidth: 560, margin: '64px auto', padding: 24,
          background: 'var(--bg-card, #fff)',
          color: 'var(--text-dark, #1a1a1a)',
          border: '1px solid var(--border-color, #e5e7eb)',
          borderRadius: 12, boxShadow: 'var(--shadow-md, 0 4px 12px rgba(0,0,0,0.25))',
        }}>
          <h1 style={{ marginTop: 0, color: 'var(--color-danger, #dc2626)' }}>Something went wrong</h1>
          <p style={{ color: 'var(--text-dark, #1a1a1a)' }}>The page hit an unexpected error. The team has been notified.</p>
          <p style={{ fontSize: 13, color: 'var(--text-light, #6b7280)', wordBreak: 'break-word' }}>
            <code style={{ background: 'var(--bg-hover, #f3f4f6)', color: 'var(--text-dark, #111)', padding: '2px 6px', borderRadius: 4 }}>
              {String(this.state.error.message || this.state.error)}
            </code>
          </p>
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button onClick={this.reset} style={{
              padding: '8px 16px', borderRadius: 6,
              background: 'var(--primary-color, #3b4cca)',
              color: 'var(--primary-text, #fff)',
              border: 'none', cursor: 'pointer',
            }}>Try again</button>
            <a href="/" style={{
              padding: '8px 16px', borderRadius: 6,
              background: 'var(--bg-hover, #eee)',
              color: 'var(--text-dark, #333)',
              border: '1px solid var(--border-color, transparent)',
              textDecoration: 'none',
            }}>Home</a>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
